import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Package,
  Download,
  Tag,
  FolderOpen,
  LayoutGrid,
  List,
  Upload,
  FileText,
  ExternalLink,
} from "lucide-react";
import api from "../lib/api";
import { ProductCatalogCard } from "../components/ProductCatalogCard";

const emptyProduct = {
  code: "",
  name: "",
  category: "",
  commercial_price: 0,
  excise_duty: 0,
  stamp_duty: 0,
  pieces_per_box: 1,
  unit_type: "pezzi",
  public_price_vat: 0,
  image_url: "",
  vat_rate: 22.0,
  barcode: "",
  palletization: "",
  datasheet_url: "",
};

function ManageCategoriesDialog({ open, onOpenChange, categories, onUpdate }) {
  const [editingCategory, setEditingCategory] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const resetForm = () => {
    setName("");
    setDescription("");
    setEditingCategory(null);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setName(category.name);
    setDescription(category.description || "");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa categoria?")) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Categoria eliminata");
      onUpdate();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, { name, description });
        toast.success("Categoria aggiornata");
      } else {
        await api.post("/categories", { name, description });
        toast.success("Categoria creata");
      }
      resetForm();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gestione Categorie</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* Form */}
          <div className="flex gap-2 items-end border-b pb-4">
            <div className="space-y-2 flex-1">
              <Label>Nome Categoria</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Es. Vini Rossi"
              />
            </div>
            <div className="space-y-2 flex-1">
              <Label>Descrizione (opzionale)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descrizione..."
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} className="bg-accent hover:bg-accent/90">
                {editingCategory ? "Aggiorna" : "Aggiungi"}
              </Button>
              {editingCategory && (
                <Button variant="ghost" onClick={resetForm}>
                  Annulla
                </Button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                      Nessuna categoria trovata
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((cat) => (
                    <TableRow key={cat.id}>
                      <TableCell className="font-medium">{cat.name}</TableCell>
                      <TableCell>{cat.description}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(cat)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductsPage() {
  const { user, isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "table"
  const [channelDiscount, setChannelDiscount] = useState(0); // Sconto canale per clienti
  const [uploadingDatasheet, setUploadingDatasheet] = useState(false);

  useEffect(() => {
    // Default to grid for clients and agents, table for admins
    if (user?.role === "client" || user?.role === "agent") setViewMode("grid");
    else setViewMode("table");
  }, [user]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    if (user?.role === "client" && user?.client_id) {
      fetchClientDiscount();
    }
  }, [categoryFilter, user]);

  const fetchClientDiscount = async () => {
    try {
      const response = await api.get(`/clients/${user.client_id}`);
      const client = response.data;
      
      // Recupera gli sconti canale dalle impostazioni
      const settingsResponse = await api.get('/settings/channel-discounts');
      const channelDiscounts = {};
      settingsResponse.data.discounts.forEach(d => {
        channelDiscounts[d.channel] = d.discount;
      });
      
      const discount = channelDiscounts[client.channel] || 0;
      setChannelDiscount(discount);
    } catch (error) {
      console.error("Error fetching client discount:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== "all") {
        params.append("category", categoryFilter);
      }
      params.append("active_only", "false");
      const response = await api.get(`/products?${params}`);
      setProducts(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei prodotti");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get("/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, formData);
        toast.success("Prodotto aggiornato con successo");
      } else {
        await api.post("/products", formData);
        toast.success("Prodotto creato con successo");
      }
      setDialogOpen(false);
      setEditingProduct(null);
      setFormData(emptyProduct);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      code: product.code,
      name: product.name,
      category: product.category,
      commercial_price: product.commercial_price,
      excise_duty: product.excise_duty,
      stamp_duty: product.stamp_duty,
      pieces_per_box: product.pieces_per_box,
      unit_type: product.unit_type || "pezzi",
      public_price_vat: product.public_price_vat,
      image_url: product.image_url || "",
      vat_rate: product.vat_rate || 22.0,
      barcode: product.barcode || "",
      palletization: product.palletization || "",
      datasheet_url: product.datasheet_url || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (product) => {
    const isPermanent = !product.is_active;
    const message = isPermanent 
      ? "Sei sicuro di voler eliminare DEFINITIVAMENTE questo prodotto? L'azione è irreversibile." 
      : "Sei sicuro di voler disattivare questo prodotto?";

    if (!window.confirm(message)) return;
    try {
      const url = `/products/${product.id}${isPermanent ? "?permanent=true" : ""}`;
      await api.delete(url);
      toast.success(isPermanent ? "Prodotto eliminato" : "Prodotto disattivato");
      fetchProducts();
    } catch (error) {
      toast.error("Errore nell'operazione");
    }
  };

  const handleDatasheetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingDatasheet(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      const response = await api.post("/products/upload-datasheet", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setFormData({ ...formData, datasheet_url: response.data.url });
      toast.success("Scheda tecnica caricata con successo");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel caricamento del file");
    } finally {
      setUploadingDatasheet(false);
    }
  };

  const handleInitDemo = async () => {
    try {
      const response = await api.post("/products/init-demo");
      toast.success(response.data.message);
      fetchProducts();
      fetchCategories();
    } catch (error) {
      toast.error("Errore nell'inizializzazione");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  // Formattazione speciale per contrassegno con 3 decimali
  const formatStampDuty = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(value);
  };

  const filteredProducts = products.filter(
    (product) =>
      product.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-primary font-bold">Catalogo Prodotti</h1>
          <p className="text-muted-foreground">
            Gestisci il catalogo prodotti dell'azienda
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && products.length === 0 && (
            <Button variant="outline" onClick={handleInitDemo} data-testid="init-demo-btn">
              <Download className="w-4 h-4 mr-2" />
              Carica Demo
            </Button>
          )}
          
          {/* View Toggle */}
          <div className="flex bg-muted rounded-lg p-1 gap-1">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
                className="px-2"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === "table" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("table")}
                className="px-2"
              >
                <List className="w-4 h-4" />
              </Button>
          </div>

          {isAdmin && (
            <>
              <Button 
                variant="outline" 
                onClick={() => setCategoryManagerOpen(true)}
                data-testid="manage-categories-btn"
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                Gestisci Categorie
              </Button>
              <ManageCategoriesDialog 
                open={categoryManagerOpen} 
                onOpenChange={setCategoryManagerOpen} 
                categories={categories} 
                onUpdate={fetchCategories}
              />

              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-accent hover:bg-accent/90"
                    onClick={() => {
                      setEditingProduct(null);
                      setFormData(emptyProduct);
                    }}
                    data-testid="add-product-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Prodotto
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>
                      {editingProduct ? "Modifica Prodotto" : "Nuovo Prodotto"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Codice Prodotto *</Label>
                        <Input
                          value={formData.code}
                          onChange={(e) =>
                            setFormData({ ...formData, code: e.target.value.toUpperCase() })
                          }
                          placeholder="Es. VR001"
                          required
                          data-testid="product-code-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Nome Prodotto *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          required
                          data-testid="product-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Categoria *</Label>
                        <Select
                          value={formData.category}
                          onValueChange={(value) =>
                            setFormData({ ...formData, category: value })
                          }
                        >
                          <SelectTrigger data-testid="product-category-select">
                            <SelectValue placeholder="Seleziona categoria" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.name}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Pezzi per Cartone *</Label>
                        <Input
                          type="number"
                          value={formData.pieces_per_box}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              pieces_per_box: parseInt(e.target.value) || 1,
                            })
                          }
                          min="1"
                          required
                          data-testid="product-pieces-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Unità di Vendita *</Label>
                        <Select
                          value={formData.unit_type}
                          onValueChange={(value) =>
                            setFormData({ ...formData, unit_type: value })
                          }
                        >
                          <SelectTrigger data-testid="product-unit-type-select">
                            <SelectValue placeholder="Seleziona unità" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pezzi">Pezzi</SelectItem>
                            <SelectItem value="cartoni">Cartoni</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Listino Commerciale (€) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.commercial_price}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              commercial_price: parseFloat(e.target.value) || 0,
                            })
                          }
                          min="0"
                          required
                          data-testid="product-commercial-price-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Listino Pubblico IVA incl. (€) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.public_price_vat}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              public_price_vat: parseFloat(e.target.value) || 0,
                            })
                          }
                          min="0"
                          required
                          data-testid="product-public-price-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Accisa (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.excise_duty}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              excise_duty: parseFloat(e.target.value) || 0,
                            })
                          }
                          min="0"
                          data-testid="product-excise-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contrassegno (€)</Label>
                        <Input
                          type="number"
                          step="0.001"
                          value={formData.stamp_duty}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              stamp_duty: parseFloat(e.target.value) || 0,
                            })
                          }
                          min="0"
                          data-testid="product-stamp-input"
                        />
                        <p className="text-xs text-muted-foreground">Supporta fino a 3 decimali (es. 0.123)</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Aliquota IVA % *</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={formData.vat_rate}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              vat_rate: parseFloat(e.target.value) || 0,
                            })
                          }
                          min="0"
                          max="100"
                          required
                          data-testid="product-vat-input"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>URL Immagine</Label>
                        <Input
                          value={formData.image_url}
                          onChange={(e) =>
                            setFormData({ ...formData, image_url: e.target.value })
                          }
                          placeholder="https://..."
                          data-testid="product-image-input"
                        />
                      </div>
                      
                      {/* Nuovi campi */}
                      <div className="space-y-2">
                        <Label>Codice a Barre</Label>
                        <Input
                          value={formData.barcode}
                          onChange={(e) =>
                            setFormData({ ...formData, barcode: e.target.value })
                          }
                          placeholder="Es. 8001234567890"
                          data-testid="product-barcode-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Palletizzazione</Label>
                        <Input
                          value={formData.palletization}
                          onChange={(e) =>
                            setFormData({ ...formData, palletization: e.target.value })
                          }
                          placeholder="Es. 50 cartoni per pallet"
                          data-testid="product-palletization-input"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Scheda Tecnica</Label>
                        <div className="flex gap-2 items-center">
                          <Input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                            onChange={handleDatasheetUpload}
                            disabled={uploadingDatasheet}
                            className="flex-1"
                            data-testid="product-datasheet-input"
                          />
                          {uploadingDatasheet && (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-accent"></div>
                          )}
                        </div>
                        {formData.datasheet_url && (
                          <div className="flex items-center gap-2 mt-2 p-2 bg-muted rounded-md">
                            <FileText className="w-4 h-4 text-accent" />
                            <span className="text-sm flex-1 truncate">Scheda tecnica caricata</span>
                            <a 
                              href={`${process.env.REACT_APP_BACKEND_URL}${formData.datasheet_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent hover:underline flex items-center gap-1 text-sm"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Visualizza
                            </a>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setFormData({ ...formData, datasheet_url: "" })}
                              className="text-destructive h-auto p-1"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                      >
                        Annulla
                      </Button>
                      <Button
                        type="submit"
                        className="bg-accent hover:bg-accent/90"
                        data-testid="product-submit-btn"
                      >
                        {editingProduct ? "Aggiorna" : "Crea"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per codice o nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-products-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-category">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
          </CardContent>
        </Card>
      ) : filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nessun prodotto trovato</p>
            {isAdmin && products.length === 0 && (
              <Button
                variant="link"
                onClick={handleInitDemo}
                className="mt-2 text-accent"
              >
                Carica prodotti demo
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-fade-in">
          {filteredProducts.map((product) => (
            <ProductCatalogCard 
              key={product.id} 
              product={product} 
              channelDiscount={user?.role === "client" ? channelDiscount : 0}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Codice</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead className="hidden md:table-cell">Categoria</TableHead>
                    <TableHead className="text-right">Listino Comm.</TableHead>
                    <TableHead className="text-right hidden lg:table-cell">Accisa</TableHead>
                    <TableHead className="text-right hidden lg:table-cell">Contr.</TableHead>
                    <TableHead className="text-right hidden sm:table-cell">Pz/Cart.</TableHead>
                    <TableHead className="text-right">Listino Pubbl.</TableHead>
                    {isAdmin && <TableHead className="w-10"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow
                      key={product.id}
                      className={!product.is_active ? "opacity-50" : ""}
                      data-testid={`product-row-${product.id}`}
                    >
                      <TableCell className="font-mono text-sm font-medium">
                        {product.code}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-xs text-muted-foreground md:hidden">
                            {product.category}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted">
                          {product.category}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(product.commercial_price)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm hidden lg:table-cell text-muted-foreground">
                        {formatCurrency(product.excise_duty)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm hidden lg:table-cell text-muted-foreground">
                        {formatStampDuty(product.stamp_duty)}
                      </TableCell>
                      <TableCell className="text-right hidden sm:table-cell">
                        {product.pieces_per_box}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-medium text-accent">
                        {formatCurrency(product.public_price_vat)}
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`product-menu-${product.id}`}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(product)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Modifica
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(product)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {product.is_active ? "Disattiva" : "Elimina definitivamente"}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
